import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useRef, useState } from "react";
import styled, { css, useTheme } from "styled-components";
import { Label, Dropdown, Icon, Picture, InputText, Spinner, Text, Badge, Break, Button, Box } from "~/components-shared";
import { useTranslation } from "react-i18next";
import { renderMultiDropdownValue } from "~/lib/helpers";
import InfiniteScroll from "react-infinite-scroll-component";
import { genericColor } from "~/lib/resolvers";
import Markdown from "~/components/Markdown";
const scrollPaneStyle = {
  overflow: "initial",
  height: "initial"
};
const Wrapper = styled.div`
  ${_ref => {
  let {
    $s,
    theme
  } = _ref;
  return theme.spacing.resolve(theme, $s);
}};
  ${_ref2 => {
  let {
    $fullWidth
  } = _ref2;
  return $fullWidth && "width: 100%";
}};
  ${_ref3 => {
  let {
    $maxWidth
  } = _ref3;
  return $maxWidth && `max-width: ${$maxWidth}`;
}};
`;
const Field = styled.div`
  position: relative;
  background-color: ${_ref4 => {
  let {
    theme,
    $color
  } = _ref4;
  return genericColor(theme, $color);
}};
  padding: 0.75rem 1.1rem;
  width: 100%;
  border: none;
  border-radius: ${_ref5 => {
  let {
    $rounded,
    theme
  } = _ref5;
  return $rounded ? "2rem" : theme.shape.borderRadius.primary;
}};
  font-size: 1.2rem;
  font-weight: ${_ref6 => {
  let {
    theme
  } = _ref6;
  return theme.typography.fontWeight.regular;
}};
  display: flex;
  align-items: center;
  ${_ref7 => {
  let {
    $outline,
    theme
  } = _ref7;
  return theme.shadows.outline(theme, $outline);
}}
  ${_ref8 => {
  let {
    $disabled
  } = _ref8;
  return $disabled && "opacity: 0.7";
}}
  z-index: 2;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transition: background-color 200ms ease-out;
    background-color: transparent;
    border-radius: ${_ref9 => {
  let {
    $rounded,
    theme
  } = _ref9;
  return $rounded ? "2em" : theme.shape.borderRadius.primary;
}};
    z-index: -1;
  }

  ${_ref10 => {
  let {
    $disabled
  } = _ref10;
  return !$disabled && css`
      &:hover:before {
        background-color: ${_ref11 => {
    let {
      theme
    } = _ref11;
    return theme.colors.action.hover;
  }};
      }
    `;
}}
`;
const FieldValue = styled.span`
  display: inline-block;
  flex-grow: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: ${_ref12 => {
  let {
    $noWrap
  } = _ref12;
  return $noWrap ? "nowrap" : "initial";
}};
  min-height: 1.5rem;
  ${_ref13 => {
  let {
    $grey,
    theme
  } = _ref13;
  return $grey && `color: ${theme.colors.text.secondary}`;
}}
`;
const Error = styled.span`
  font-size: 0.8rem;
  display: inline-block;
  margin-left: 0.5rem;
  color: ${_ref14 => {
  let {
    theme
  } = _ref14;
  return theme.colors.danger.main;
}};
`;
const Select = styled.select`
  height: 0;
  width: 0;
`;
const Color = styled.div`
  width: 1rem;
  height: 1rem;
  background-color: ${_ref15 => {
  let {
    $color,
    theme
  } = _ref15;
  return genericColor(theme, $color);
}};
  border-radius: 50%;
  margin-right: 0.6rem;
`;
const StyledIcon = styled(Icon)`
  pointer-events: none;
`;
const InputSelect = _ref16 => {
  let {
    label,
    multiple = false,
    value = multiple ? [] : undefined,
    options,
    direction = "bottomRight",
    error,
    onChange = () => false,
    searchValue = "",
    mutuallyExclusive,
    onSearch,
    required = false,
    disabled = false,
    rounded = false,
    fullSelections = false,
    onLoadMore = () => false,
    hasNextPage = false,
    color = "translucent",
    maxWidth,
    fullWidth = false,
    withSearch = false,
    loading = false,
    elemBefore,
    elemAfter,
    handlerElemBefore,
    handlerElemAfter,
    noOptionsText,
    s,
    children,
    placeholder = "",
    labelProps,
    dropdownProps,
    outline,
    ...rest
  } = _ref16;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const [internalSearchValue, setInternalSearchValue] = useState(searchValue);
  const valueRef = useRef();
  let optionList = [];
  let filteredOptionList;
  let selectedOption;
  useEffect(() => {
    setInternalSearchValue(searchValue);
  }, [searchValue]);
  if (Array.isArray(options)) {
    optionList = options;
  } else {
    optionList = Object.keys(options).map(key => ({
      id: key,
      value: options[key]
    }));
  }
  filteredOptionList = optionList.filter(_ref17 => {
    let {
      hidden
    } = _ref17;
    return !hidden;
  });
  if (!onSearch || typeof onSearch !== "function") {
    filteredOptionList = optionList.filter(item => !item.hidden && `${item.value}`.toLowerCase().includes(internalSearchValue.toLowerCase()));
  }
  const handleSearch = query => {
    setInternalSearchValue(query);
    if (onSearch && typeof onSearch === "function") {
      onSearch(query);
    }
  };
  const handleChange = id => {
    if (multiple && value && Array.isArray(value)) {
      if (mutuallyExclusive && mutuallyExclusive.length > 0 && typeof id === "string" && mutuallyExclusive.includes(id)) {
        onChange(value.includes(id) ? value.filter(item => item !== id) : [...value.filter(item => !mutuallyExclusive.includes(item)), id]);
      } else {
        onChange(value.includes(id) ? value.filter(item => item !== id) : [...value, id]);
      }
    } else {
      onChange(id);
    }
  };
  const handleBulkAdd = () => {
    if (multiple) {
      const newValue = filteredOptionList.filter(option => !option.disabled && !value?.includes(option.id)).map(option => option.id);
      onChange([...value, ...newValue]);
    }
  };
  if (multiple && Array.isArray(value)) {
    selectedOption = !fullSelections ? renderMultiDropdownValue(valueRef, optionList.filter(_ref18 => {
      let {
        id
      } = _ref18;
      return value.includes(id);
    })) : optionList.filter(_ref19 => {
      let {
        id
      } = _ref19;
      return value.includes(id);
    }).map(option => /*#__PURE__*/React.createElement(Badge, {
      key: option.id,
      display: "inline-block",
      color: disabled ? "translucent" : "translucentPrimary",
      weight: "semiBold",
      fontSize: 3,
      s: {
        pl: 3,
        py: 0.5,
        pr: disabled ? 3 : 0.5
      }
    }, /*#__PURE__*/React.createElement(Box, {
      direction: "row",
      align: "center",
      justify: "center"
    }, option.value, !disabled && /*#__PURE__*/React.createElement(Button, {
      s: {
        px: 1,
        py: 0,
        ml: 0.5,
        mr: 0
      },
      onClick: e => {
        e.stopPropagation();
        handleChange(option.id);
      },
      fontSize: 3,
      color: "translucentPrimary",
      display: "inline-block",
      rounded: true,
      outline: false
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 3,
      src: "cross",
      s: {
        mt: -0.2
      }
    })))));
  } else {
    selectedOption = optionList.find(_ref20 => {
      let {
        id
      } = _ref20;
      return id === value;
    })?.value || value;
  }
  const customHandler = (toggle, isOpen) => /*#__PURE__*/React.createElement(Field, {
    $rounded: rounded,
    $color: color,
    $outline: isOpen || outline,
    onClick: disabled ? undefined : toggle,
    $disabled: disabled
  }, !selectedOption || selectedOption === "" || !selectedOption.length ? /*#__PURE__*/React.createElement(FieldValue, {
    $grey: true,
    $noWrap: true
  }, handlerElemBefore, placeholder, handlerElemAfter) : /*#__PURE__*/React.createElement(FieldValue, {
    ref: valueRef,
    $noWrap: !fullSelections
  }, /*#__PURE__*/React.createElement(Box, {
    gap: 1,
    wrap: "wrap"
  }, handlerElemBefore, selectedOption, handlerElemAfter)), /*#__PURE__*/React.createElement(Select, _extends({
    disabled: disabled,
    multiple: multiple
  }, rest)), loading ? /*#__PURE__*/React.createElement(Spinner, {
    scale: 5,
    s: {
      ml: 1
    },
    color: theme.colors.primary.main
  }) : /*#__PURE__*/React.createElement(Icon, {
    src: "chevron",
    scale: 5,
    color: theme.colors.text.secondary,
    s: {
      ml: 1
    },
    rotate: isOpen ? 0 : 180
  }));
  return /*#__PURE__*/React.createElement(Wrapper, {
    $s: s,
    $disabled: disabled,
    $maxWidth: maxWidth,
    $fullWidth: fullWidth
  }, !!label && /*#__PURE__*/React.createElement(Label, _extends({
    required: required,
    disabled: disabled,
    s: {
      mb: 2
    }
  }, labelProps), label), /*#__PURE__*/React.createElement(Dropdown, _extends({
    direction: direction,
    customHandler: customHandler,
    onClose: () => setInternalSearchValue("")
  }, dropdownProps), _ref21 => {
    let {
      isOpen,
      onItemClick
    } = _ref21;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen,
      id: "member-scroll-target"
    }, withSearch && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      noHover: true,
      sticky: true
    }, /*#__PURE__*/React.createElement(InputText, {
      autoFocus: true,
      value: internalSearchValue,
      onChange: handleSearch,
      elemBefore: /*#__PURE__*/React.createElement(Icon, {
        src: "search",
        s: {
          mr: 2
        }
      }),
      placeholder: t("common.actions.search"),
      elemAfter: multiple && !!internalSearchValue && !!filteredOptionList.length && /*#__PURE__*/React.createElement(Button, {
        onClick: handleBulkAdd,
        fontSize: 3,
        color: "transparent",
        s: {
          ml: 1,
          px: 2,
          py: 0.5,
          mr: -1
        },
        outline: false
      }, t("InputSelect.selectFound")),
      fullWidth: true,
      clearButton: true
    })), elemBefore && elemBefore, filteredOptionList.length ? /*#__PURE__*/React.createElement(InfiniteScroll, {
      style: scrollPaneStyle,
      dataLength: filteredOptionList.length || 0,
      next: onLoadMore,
      hasMore: hasNextPage,
      loader: /*#__PURE__*/React.createElement(Spinner, {
        s: {
          mt: 6
        },
        center: true
      }),
      scrollableTarget: "member-scroll-target"
    }, filteredOptionList.map(option => `${option.id}`.includes("lineBreak") ? /*#__PURE__*/React.createElement(Break, null, option.value && /*#__PURE__*/React.createElement(Text, {
      fontSize: 3
    }, option.value)) : /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: () => {
        if (option.disabled) {
          return;
        }
        if (!multiple) {
          onItemClick();
        }
        handleChange(option.id);
      },
      key: option.id,
      selected: option.selected ? true : multiple ? value?.includes(option.id) : value === option.id,
      textWrap: true,
      indent: option.indent
    }, multiple && /*#__PURE__*/React.createElement(StyledIcon, {
      src: option.selected || value?.includes(option.id) ? "checkboxFill" : "checkbox",
      color: option.disabled ? "disabled" : "primary",
      s: {
        mr: 2
      }
    }), option.showImage && /*#__PURE__*/React.createElement(Picture, {
      name: `${option.value}`,
      src: option.image,
      color: option.imageColor || theme.colors.primary.main,
      width: option.pre || option.post ? "46px" : "32px",
      height: option.pre || option.post ? "46px" : "32px",
      s: {
        mr: 2
      },
      border: "circled"
    }), option.color && /*#__PURE__*/React.createElement(Color, {
      $color: option.color || theme.colors.primary.main
    }), /*#__PURE__*/React.createElement(Box, {
      fullWidth: true
    }, option.pre && /*#__PURE__*/React.createElement(Text, {
      color: theme.colors.primary.main,
      fontSize: 3,
      weight: "semiBold"
    }, option.pre), option.pre || option.post ? /*#__PURE__*/React.createElement(Box, {
      align: "center",
      justify: "space-between",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Text, {
      display: "block",
      fontSize: 4,
      lineHeight: 5,
      weight: "semiBold"
    }, option.value), /*#__PURE__*/React.createElement(Text, null, option.accessory)) : /*#__PURE__*/React.createElement(Box, {
      align: "center",
      justify: "space-between",
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Text, null, option.value), /*#__PURE__*/React.createElement(Text, null, option.accessory)), option.post && /*#__PURE__*/React.createElement(Text, {
      color: "textSecondary",
      fontSize: 3,
      weight: "semiBold"
    }, option.post)), option.tip && /*#__PURE__*/React.createElement(Icon, {
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": option.tip,
      src: "infoOutline",
      color: theme.colors.text.secondary,
      s: {
        ml: 1
      }
    })))) : /*#__PURE__*/React.createElement(Markdown, {
      color: "textSecondary",
      display: "block",
      s: {
        p: 3
      },
      text: noOptionsText || t("InputSelect.noOptions")
    }), elemAfter && elemAfter);
  }), error && /*#__PURE__*/React.createElement(Error, null, error));
};
export default InputSelect;